import { Box, TextField, Typography } from '@mui/material';
import useOrganizations from 'hooks/useOrganizations';
import { useTranslation } from 'next-i18next';
import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import type { FC } from 'react';
import type { DefaultPopupProps } from 'utils/props';

import Button from 'components/Button';
import ErrorText from 'components/ErrorText';
import Modal from 'components/Modal';
import StatusMessageBox from 'components/StatusMessageBox';

import { useAppDispatch } from 'redux-store';
import getAllOrganizationsThunk from 'redux-store/thunks/organizations/getAllOrganizationsThunk';
import updateOrganizationThunk from 'redux-store/thunks/organizations/updateOrganizationThunk';

export interface RenameOrganizationModalProps extends DefaultPopupProps {
  organizationId: number | null;
}

const RenameOrganizationModal: FC<RenameOrganizationModalProps> = ({
  organizationId,
  onClose,
  open,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const groupNameInputRef = useRef<HTMLInputElement>(null);

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [inputError, setInputError] = useState<string | null>(null);

  const organization = useOrganizations(organizationId ?? undefined);

  const currentName = useMemo(() => organization?.name ?? '', [organization]);

  const [name, setName] = useState<string>(currentName);

  useEffect(() => {
    if (currentName) {
      setName(currentName);
    }
  }, [currentName]);

  const validateName = useCallback(
    (inputName: string): string => {
      if (!inputName) {
        return t('common:pages.organization.errors.name_required');
      }

      if (inputName.length > 50) {
        return t(
          'common:pages.organization.errors.organization_name_max_length',
        );
      }

      return '';
    },
    [t],
  );

  const handleChangeName = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newName = e.target.value;
      const validationError = validateName(newName);
      setInputError(validationError);
      setName(newName);
    },
    [validateName],
  );

  const handleSave = useCallback(async () => {
    if (isLoading) return;
    if (organizationId === null) return;

    const validationError = validateName(name);

    if (validationError) {
      setInputError(validationError);

      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await dispatch(
        updateOrganizationThunk({
          organizationId: organizationId,
          data: { name },
        }),
      ).unwrap();

      if (result.success) {
        await dispatch(getAllOrganizationsThunk({})).unwrap();
        setSuccess(true);

        setTimeout(() => {
          setSuccess(false);
          onClose();
        }, 1250);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err as string);
    } finally {
      setLoading(false);
    }
  }, [dispatch, organizationId, isLoading, name, onClose, validateName]);

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        groupNameInputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  return (
    <Modal open={open} onClose={onClose}>
      {!success ? (
        <Box
          height="100%"
          width="100%"
          display="flex"
          flexDirection="column"
          alignItems="center"
          sx={{ overflowY: 'auto' }}
          data-testid="rename-organization-modal"
        >
          <Box mb={4} width="100%">
            <Typography
              variant="h2"
              textAlign="center"
              mb={2}
              data-testid="rename-organisation-modal-title"
            >
              {t('common:pages.organization.rename_organization.title')}
            </Typography>
            <Typography variant="body1" mb={2} textAlign="center">
              {t('common:pages.organization.rename_organization.description')}
            </Typography>
          </Box>
          <Box mb={4} width="100%">
            <form
              onSubmit={e => {
                e.preventDefault();
              }}
            >
              <TextField
                variant="filled"
                fullWidth
                label={t('common:pages.organization.organization_name')}
                value={name}
                onChange={handleChangeName}
                inputProps={{ 'data-testid': 'new-name-input' }}
                error={Boolean(inputError)}
                helperText={inputError}
                inputRef={groupNameInputRef}
              />
            </form>
          </Box>
          <ErrorText error={error} mb={2} />
          <Box width="100%" display="flex" flexDirection="row" gap={2} pt={2}>
            <Button
              variant="flat"
              color="neutral"
              fullWidth
              onClick={onClose}
              size="large"
              data-testid="cancel-button"
            >
              {t('common:cancel')}
            </Button>
            <Button
              variant="flat"
              fullWidth
              size="large"
              onClick={handleSave}
              disabled={isLoading || Boolean(inputError)}
              data-testid="submit"
            >
              {t('common:save')}
            </Button>
          </Box>
        </Box>
      ) : (
        <StatusMessageBox
          type="success"
          message={t('common:pages.organization.rename_organization.success')}
        />
      )}
    </Modal>
  );
};

export default RenameOrganizationModal;
